// SectionItemsList.jsx — COMPLETE FINAL FILE
import React, { useMemo, useCallback } from 'react';
import { MaterialReactTable } from 'material-react-table';
import { Box, Typography, CircularProgress, Alert, Stack, Button, IconButton } from '@mui/material';
import { Delete, Save } from '@mui/icons-material';
import { useDispatch } from 'react-redux';
import { updateCustomFormOrder } from '../../../actions/apiCustomForm';
import _ from 'lodash';

const generateUniqueId = () => `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

const SectionItemsList = ({
  sectionId,
  items,
  sections,
  setSections,
  work_item_presets,
}) => {
  const dispatch = useDispatch();
  const [presetsReady, setPresetsReady] = React.useState(false);

  React.useEffect(() => {
    if (work_item_presets?.length > 0) {
      requestAnimationFrame(() => setPresetsReady(true));
    }
  }, [work_item_presets]);

  const tableData = useMemo(() => {
    if (!Array.isArray(items)) return [];

    return items
      .filter(item => {
        const pid = Number(item.presetId);
        return !isNaN(pid) && _.some(work_item_presets, { id: pid });
      })
      .map(item => ({
        id: item.id,
        sectionId,
        presetId: item.presetId,
        propertyId: item.propertyId,
      }));
  }, [items, work_item_presets, sectionId]);

  const columns = useMemo(
    () => [
      {
        accessorKey: 'name',
        header: 'Preset',
        size: 200,
        enableEditing: false,
        Cell: ({ row }) => {
          const preset = _.find(work_item_presets, { id: Number(row.original.presetId) });
          return preset?.name || 'Unknown';
        },
      },
      {
        accessorKey: 'action',
        header: '',
        size: 80,
        enableEditing: false,
        Cell: ({ row }) => (
          <IconButton
            size="small"
            onClick={() => {
              const newItems = sections[sectionId].items.filter(i => i.id !== row.original.id);
              setSections(prev => ({
                ...prev,
                [sectionId]: { ...prev[sectionId], items: newItems },
              }));
            }}
          >
            <Delete fontSize="small" />
          </IconButton>
        ),
      },
    ],
    [work_item_presets, sections, sectionId, setSections]
  );

  const saveSectionOrder = () => {
    const propertyIds = sections[sectionId].items
      .filter(i => i.propertyId)
      .map(i => i.propertyId);
    if (propertyIds.length > 0) {
      dispatch(updateCustomFormOrder(propertyIds));
    }
  };

  const handleDrop = useCallback(
    e => {
      e.preventDefault();
      e.stopPropagation();
      const presetIdStr = e.dataTransfer.getData('presetId');
      if (!presetIdStr) return;
      const presetId = Number(presetIdStr);
      if (isNaN(presetId)) return;

      const newItem = { id: generateUniqueId(), presetId, propertyId: null };
      setSections(prev => ({
        ...prev,
        [sectionId]: {
          ...prev[sectionId],
          items: [...(prev[sectionId]?.items || []), newItem],
        },
      }));
    },
    [sectionId, setSections]
  );

  return (
    <>
      {/* ITEMS HEADER */}
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
        <Typography variant="subtitle2">Items</Typography>
        {items.some(i => i.propertyId) && (
          <Button size="small" startIcon={<Save />} onClick={saveSectionOrder}>
            Save Order
          </Button>
        )}
      </Stack>

      {/* DROP ZONE */}
      <Box
        onDrop={handleDrop}
        onDragOver={e => e.preventDefault()}
        sx={{
          minHeight: 80,
          border: '2px dashed #ccc',
          borderRadius: 2,
          bgcolor: '#fafafa',
          p: 2,
          mb: 2,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: '#999',
          fontStyle: 'italic',
        }}
      >
        <Typography variant="caption">Drop presets here to add to section</Typography>
      </Box>

      {/* ITEMS WARNING */}
      {items.length > tableData.length && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          {items.length - tableData.length} item(s) reference invalid or deleted presets and were hidden.
        </Alert>
      )}

      {/* ITEMS TABLE */}
      {tableData.length === 0 ? (
        <Typography color="text.secondary" sx={{ p: 2 }}>
          No presets in this section.
        </Typography>
      ) : !presetsReady ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
          <CircularProgress size={20} />
        </Box>
      ) : (
        <MaterialReactTable
          columns={columns}
          data={tableData}
          enableRowOrdering
          enableEditing={false}
          getRowId={row => row.id}
          initialState={{ density: 'compact' }}
          muiTableContainerProps={{ sx: { maxHeight: 400 } }}
          enableStickyHeader
          enableColumnResizing
          enableRowVirtualization
          rowVirtualizerProps={{ overscan: 5 }}
          memoMode="all"
          muiRowDragHandleProps={({ table }) => ({
            onDragEnd: () => {
              const { draggingRow, hoveredRow } = table.getState();
              if (!hoveredRow || !draggingRow) return;
              const rows = [...table.getRowModel().rows];
              rows.splice(hoveredRow.index, 0, rows.splice(draggingRow.index, 1)[0]);
              const updated = rows.map(r => items.find(i => i.id === r.original.id)).filter(Boolean);
              setSections(prev => ({
                ...prev,
                [sectionId]: { ...prev[sectionId], items: updated },
              }));
            },
          })}
        />
      )}
    </>
  );
};

export default SectionItemsList;